import DashboardLayout from '../../components/DashboardLayout';
import { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import axiosInstance from '../../utils/axiosInstance';

export default function Positions() {
    const basePath = '/admin/positions';
    const [positions, setPositions] = useState([]);
    const [totalPositions, setTotalPositions] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedPosition, setSelectedPosition] = useState(null);
    const [positionName, setPositionName] = useState('');
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [flash, setFlash] = useState(null);

    // Initial load
    useEffect(() => {
        const fetchPositions = async () => {
            try {
                const res = await axiosInstance.get(`${basePath}`);
                setPositions(res.data.positions || []);
                setTotalPositions(res.data.totalPositions || (res.data.positions ? res.data.positions.length : 0));
            } catch (error) {
                console.error('Error fetching positions:', error);
                setFlash({ error: 'Failed to load positions' });
            }
        };
        fetchPositions();
    }, []);

    // Handle flash messages
    useEffect(() => {
        if (flash?.success) {
            Swal.fire({
                icon: 'success',
                title: 'Success!',
                text: flash.success,
                confirmButtonColor: '#4f46e5',
            });
        }
        if (flash?.error) {
            Swal.fire({
                icon: 'error',
                title: 'Error!',
                text: flash.error,
                confirmButtonColor: '#ef4444',
            });
        }
    }, [flash]);

    // Filter positions based on search
    const filteredPositions = positions.filter((position) =>
        position.positionName.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Validate position name
    const validatePositionName = async (name, excludeId) => {
        if (!name.trim()) {
            setErrors({ positionName: 'Position name is required' });
            return false;
        }

        if (name.length > 255) {
            setErrors({ positionName: 'Position name must not exceed 255 characters' });
            return false;
        }

        // Check if name already exists
        try {
            const response = await axiosInstance.get(
                `${basePath}/check-name`,
                { params: { positionName: name, positionId: excludeId } }
            );
            if (response.data?.exists) {
                setErrors({ positionName: 'This position name already exists' });
                return false;
            }
        } catch (error) {
            console.error('Error checking position name:', error);
        }

        setErrors({});
        return true;
    };

    // Handle add position
    const handleAdd = () => {
        setPositionName('');
        setErrors({});
        setShowAddModal(true);
    };

    // Handle edit position
    const handleEdit = (position) => {
        setSelectedPosition(position);
        setPositionName(position.positionName);
        setErrors({});
        setShowEditModal(true);
    };

    // Handle delete position
    const handleDelete = (position) => {
        Swal.fire({
            title: 'Are you sure?',
            text: `Do you want to delete "${position.positionName}"?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#ef4444',
            cancelButtonColor: '#6b7280',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'Cancel',
        }).then((result) => {
            if (result.isConfirmed) {
                axiosInstance
                    .delete(`${basePath}/${position.positionId}`)
                    .then(() => {
                        setPositions((prev) => prev.filter((p) => p.positionId !== position.positionId));
                        setTotalPositions((prev) => Math.max(0, prev - 1));
                        setFlash({ success: 'Position deleted successfully' });
                    })
                    .catch((error) => {
                        console.error('Error deleting position:', error);
                        setFlash({ error: 'Failed to delete position' });
                    });
            }
        });
    };

    // Submit add form
    const handleSubmitAdd = async (e) => {
        e.preventDefault();
        
        const isValid = await validatePositionName(positionName);
        if (!isValid) return;

        setIsSubmitting(true);
        axiosInstance
            .post(basePath, { positionName })
            .then((res) => {
                const newPosition = res.data?.position;
                setPositions((prev) => (newPosition ? [...prev, newPosition] : prev));
                setTotalPositions((prev) => prev + 1);
                setShowAddModal(false);
                setPositionName('');
                setErrors({});
                setFlash({ success: 'Position added successfully' });
            })
            .catch((error) => {
                console.error('Error adding position:', error);
                const serverErrors = error.response?.data?.errors || {};
                setErrors(serverErrors);
                setFlash({ error: 'Failed to add position' });
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    // Submit edit form
    const handleSubmitEdit = async (e) => {
        e.preventDefault();
        
        if (!selectedPosition) return;

        const isValid = await validatePositionName(positionName, selectedPosition.positionId);
        if (!isValid) return;

        setIsSubmitting(true);
        axiosInstance
            .put(`${basePath}/${selectedPosition.positionId}`, { positionName })
            .then((res) => {
                const updated = res.data?.position;
                setPositions((prev) =>
                    prev.map((p) => (p.positionId === selectedPosition.positionId ? (updated || { ...p, positionName }) : p))
                );
                setShowEditModal(false);
                setPositionName('');
                setSelectedPosition(null);
                setErrors({});
                setFlash({ success: 'Position updated successfully' });
            })
            .catch((error) => {
                console.error('Error updating position:', error);
                const serverErrors = error.response?.data?.errors || {};
                setErrors(serverErrors);
                setFlash({ error: 'Failed to update position' });
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    return (
        <DashboardLayout title="Positions">
            <div className="space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Positions Management</h1>
                        <p className="mt-1 text-sm text-gray-600">
                            Manage staff positions and roles
                        </p>
                    </div>
                    <button
                        onClick={handleAdd}
                        className="flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 transition-colors"
                    >
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Add Position
                    </button>
                </div>

                {/* Stats Card */}
                <div className="rounded-lg bg-white p-6 shadow-sm border border-gray-200">
                    <div className="flex items-center gap-4">
                        <div className="rounded-lg bg-indigo-100 p-3">
                            <svg className="h-6 w-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                        </div>
                        <div>
                            <p className="text-sm font-medium text-gray-600">Total Positions</p>
                            <p className="text-2xl font-bold text-gray-900">{totalPositions}</p>
                        </div>
                    </div>
                </div>

                {/* Search Bar */}
                <div className="rounded-lg bg-white p-4 shadow-sm border border-gray-200">
                    <div className="relative">
                        <input
                            type="text"
                            placeholder="Search positions..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full rounded-lg border border-gray-300 py-2 pl-10 pr-4 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                        <svg
                            className="absolute left-3 top-2.5 h-5 w-5 text-gray-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                            />
                        </svg>
                    </div>
                </div>

                {/* Positions Table */}
                <div className="overflow-hidden rounded-lg bg-white shadow-sm border border-gray-200">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                    #
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                    Position Name
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                    Created At
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                                    Updated At
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 bg-white">
                            {filteredPositions.length > 0 ? (
                                filteredPositions.map((position, index) => (
                                    <tr key={position.positionId} className="hover:bg-gray-50 transition-colors">
                                        <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">
                                            {index + 1}
                                        </td>
                                        <td className="whitespace-nowrap px-6 py-4">
                                            <div className="text-sm font-medium text-gray-900">
                                                {position.positionName}
                                            </div>
                                        </td>
                                        <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                                            {new Date(position.createdAt).toLocaleDateString('en-GB', {
                                                day: '2-digit',
                                                month: 'short',
                                                year: 'numeric',
                                            })}
                                        </td>
                                        <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                                            {new Date(position.updatedAt).toLocaleDateString('en-GB', {
                                                day: '2-digit',
                                                month: 'short',
                                                year: 'numeric',
                                            })}
                                        </td>
                                        <td className="whitespace-nowrap px-6 py-4 text-right text-sm font-medium">
                                            <div className="flex items-center justify-end gap-2">
                                                <button
                                                    onClick={() => handleEdit(position)}
                                                    className="rounded-lg bg-blue-100 px-3 py-1.5 text-blue-700 hover:bg-blue-200 transition-colors"
                                                    title="Edit"
                                                >
                                                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                    </svg>
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(position)}
                                                    className="rounded-lg bg-red-100 px-3 py-1.5 text-red-700 hover:bg-red-200 transition-colors"
                                                    title="Delete"
                                                >
                                                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                    </svg>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={5} className="px-6 py-12 text-center">
                                        <div className="flex flex-col items-center justify-center text-gray-500">
                                            <svg className="h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                                            </svg>
                                            <p className="mt-2 text-sm">No positions found</p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Add Position Modal */}
            {showAddModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                    <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
                        <h2 className="text-xl font-bold text-gray-900 mb-4">Add New Position</h2>
                        <form onSubmit={handleSubmitAdd}>
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Position Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={positionName}
                                    onChange={(e) => setPositionName(e.target.value)}
                                    className={`w-full rounded-lg border ${errors.positionName ? 'border-red-500' : 'border-gray-300'} px-4 py-2 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                                    placeholder="Enter position name"
                                    autoFocus
                                />
                                {errors.positionName && (
                                    <p className="mt-1 text-sm text-red-500">{errors.positionName}</p>
                                )}
                            </div>
                            <div className="flex justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowAddModal(false);
                                        setPositionName('');
                                        setErrors({});
                                    }}
                                    className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                                    disabled={isSubmitting}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 transition-colors disabled:opacity-50"
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? 'Adding...' : 'Add Position'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Edit Position Modal */}
            {showEditModal && selectedPosition && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                    <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
                        <h2 className="text-xl font-bold text-gray-900 mb-4">Edit Position</h2>
                        <form onSubmit={handleSubmitEdit}>
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Position Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={positionName}
                                    onChange={(e) => setPositionName(e.target.value)}
                                    className={`w-full rounded-lg border ${errors.positionName ? 'border-red-500' : 'border-gray-300'} px-4 py-2 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                                    placeholder="Enter position name"
                                    autoFocus
                                />
                                {errors.positionName && (
                                    <p className="mt-1 text-sm text-red-500">{errors.positionName}</p>
                                )}
                            </div>
                            <div className="flex justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowEditModal(false);
                                        setPositionName('');
                                        setSelectedPosition(null);
                                        setErrors({});
                                    }}
                                    className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                                    disabled={isSubmitting}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 transition-colors disabled:opacity-50"
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? 'Updating...' : 'Update Position'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </DashboardLayout>
    );
}
